import * as React from 'react';
import { Body, Input, TableBody } from 'vet-bones/design-system/components';

import styles from 'src/components/resourceDialog/styles/resourcePreviewTable.module.scss';

/**
 * Base field type for resource preview tables.
 * Extended by specific field types (e.g., ProjectFileUploadField, ProjectVirtualGraphField).
 */
export type ResourceField = {
  value: string;
  label: string;
};

export type ResourcePreviewTableProps<TField extends ResourceField> = {
  /** The fields/columns to display */
  fields: TField[];
  /** The data rows to display */
  rows: string[][];
  /** Callback when a field label is changed (enables editing when provided) */
  onFieldLabelChange?: (value: string, label: string) => void;
  /** Whether to disable editing even if onFieldLabelChange is provided */
  disableEditing?: boolean;
  /** Optional render function for extra content in the header (e.g., statistics) */
  renderHeaderExtra?: (field: TField) => React.ReactNode;
  /** Test ID for the table container */
  testId?: string;
  /** Function to generate test IDs for column headers */
  headerTestIdFn?: (fieldValue: string) => string;
};

/**
 * A unified preview table component for displaying resource data.
 *
 * This component consolidates the common table pattern used by:
 * - ProjectFileUploadTable (CSV previews)
 * - VirtualGraphPreviewTable (Virtual Graph previews)
 *
 * Features:
 * - Editable column headers (when onFieldLabelChange is provided)
 * - Optional statistics/extra content per column via renderHeaderExtra
 * - Flexible test ID support
 *
 * @example
 * ```tsx
 * // For CSV preview with statistics
 * <ResourcePreviewTable
 *   fields={fields}
 *   rows={rows}
 *   onFieldLabelChange={handleLabelChange}
 *   renderHeaderExtra={(field) => (
 *     <div className={styles.statistics}>
 *       <Body size="sm">{`${field.statistics.distinct} distinct`}</Body>
 *     </div>
 *   )}
 *   testId={TEST_IDS.table}
 *   headerTestIdFn={getColumnLabelTestId}
 * />
 *
 * // For Virtual Graph preview (simple)
 * <ResourcePreviewTable
 *   fields={fields}
 *   rows={rows}
 *   onFieldLabelChange={handleLabelChange}
 * />
 * ```
 */
export const ResourcePreviewTable = <TField extends ResourceField>({
  fields,
  rows,
  onFieldLabelChange,
  disableEditing,
  renderHeaderExtra,
  testId,
  headerTestIdFn,
}: ResourcePreviewTableProps<TField>): React.ReactElement => {
  const canEdit = !disableEditing && onFieldLabelChange !== undefined;

  const header = React.useMemo(
    () => (
      <>
        {fields.map((field) => (
          <th key={field.value} className={styles.header}>
            <div className={styles.headerContent}>
              <div
                className={styles.headerName}
                data-testid={headerTestIdFn?.(field.value)}
              >
                {canEdit ? (
                  <Input
                    className={styles.nameEditable}
                    clearable={false}
                    id={`field-label-${field.value}`}
                    label=""
                    minimal
                    onChange={(e) =>
                      onFieldLabelChange?.(field.value, e.target.value)
                    }
                    placeholder={field.label}
                    size="md"
                    value={field.label}
                  />
                ) : (
                  <Body className={styles.nameText} ellipsize size="md">
                    {field.label}
                  </Body>
                )}
              </div>
              {renderHeaderExtra?.(field)}
            </div>
          </th>
        ))}
      </>
    ),
    [canEdit, fields, headerTestIdFn, onFieldLabelChange, renderHeaderExtra]
  );

  const body = React.useMemo(
    () =>
      rows.map((row, rowIdx) => (
        // eslint-disable-next-line react/no-array-index-key -- Preview table rows have no unique ID and are read-only
        <React.Fragment key={row.join('|') || rowIdx}>
          {row.map((cellValue, colIdx) => (
            <td key={fields[colIdx]?.value || colIdx} className={styles.cell}>
              <Body ellipsize size="md">
                {cellValue ?? ''}
              </Body>
            </td>
          ))}
        </React.Fragment>
      )),
    [rows, fields]
  );

  return (
    <div
      className={styles.resourcePreviewTable}
      data-heap-redact-attributes="title"
      data-testid={testId}
    >
      <TableBody body={body} header={header} size="md" />
    </div>
  );
};
